import * as React from "react";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { cn } from "@/lib/utils";

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

function Sheet({ open, onOpenChange, children }: SheetProps) {
  return (
    <BaseDialog.Root open={open} onOpenChange={onOpenChange}>
      <BaseDialog.Portal>{children}</BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

const SheetOverlay = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { open: boolean; onClose: () => void }
>(({ className, open: _open, onClose, ...props }, ref) => {
  return (
    <BaseDialog.Backdrop
      ref={ref}
      className={cn(
        "fixed inset-0 z-40 bg-white/70 backdrop-blur-sm data-[starting-style]:opacity-0 data-[ending-style]:opacity-0 transition-opacity duration-200 ease-out",
        className,
      )}
      onClick={onClose}
      {...props}
    />
  );
});
SheetOverlay.displayName = "SheetOverlay";

const SheetContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { open: boolean; side?: "left" | "right" }
>(({ className, open, side = "left", ...props }, ref) => {
  const sideClass = side === "right" ? "right-0 border-l-2 border-black border-r-0" : "left-0 border-r-2 border-black";
  const transformClass =
    side === "right"
      ? open
        ? "translate-x-0"
        : "translate-x-full"
      : open
        ? "translate-x-0"
        : "-translate-x-full";
  const dataTransformClass =
    side === "right"
      ? "data-[starting-style]:translate-x-full data-[ending-style]:translate-x-full"
      : "data-[starting-style]:-translate-x-full data-[ending-style]:-translate-x-full";

  return (
    <BaseDialog.Popup
      ref={ref}
      className={cn(
        "fixed top-0 bottom-0 z-50 w-[280px] bg-white transition-transform duration-[250ms] ease-[cubic-bezier(0,0,0.2,1)] shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]",
        sideClass,
        transformClass,
        dataTransformClass,
        className,
      )}
      {...props}
    />
  );
});
SheetContent.displayName = "SheetContent";

export { Sheet, SheetOverlay, SheetContent };
